/**
 * DR Video Testimonials
 * Carousel with video playback, deck-style stacking animation, and progress tracking.
 *
 * Each testimonials root contains:
 * - Track with scrollable cards
 * - Dots navigation
 * - Video playback with lazy loading
 * - Deck-style transform effects
 */
(function () {
  'use strict';

  /**
   * Initialize a single testimonials instance
   * @param {HTMLElement} root - The testimonials root element [data-dr-testimonials-root]
   */
  function initTestimonials(root) {
    // Idempotent guard: skip if already initialized
    if (root.dataset.initialized === '1') {
      return;
    }
    root.dataset.initialized = '1';

    var track = root.querySelector('[data-testimonials-track]');
    if (!track) return;

    var cards = Array.from(track.querySelectorAll('.dr-video-testimonials__card'));
    var dotsContainer = root.querySelector('[data-testimonials-dots]');
    if (!cards.length || !dotsContainer) return;

    var dots = [];
    var activeIndex = 0;

    // Small random tilt jitter per card (stable for this page load)
    var tiltJitterMax = 1; // degrees
    cards.forEach(function (card) {
      var jitter = (Math.random() * 2 - 1) * tiltJitterMax; // -1..+1
      card.dataset.deckTiltJitter = jitter.toFixed(3);
    });

    /* ---------- Exact side padding so first/last can center ---------- */
    function updateTrackSidePadding() {
      if (!cards.length) return;

      // width of visible scroll area
      var trackWidth = track.clientWidth;
      // actual rendered width of a card (includes max-width, gap doesn't matter)
      var cardWidth = cards[0].offsetWidth;

      var sidePadding = Math.max(0, (trackWidth - cardWidth) / 2);

      // Use CSS custom property - works with scroll-padding-inline for iOS fix
      track.style.setProperty('--track-side-padding', sidePadding + 'px');
    }

    /* ---------- Dots "string" line ---------- */
    function updateDotsLine() {
      if (!dots.length) return;

      if (dots.length === 1) {
        // Single bead – no visible line needed
        dotsContainer.style.setProperty('--dots-line-width', '0px');
        dotsContainer.style.setProperty('--dots-line-left', '50%');
        return;
      }

      var first = dots[0];
      var last = dots[dots.length - 1];

      var firstRect = first.getBoundingClientRect();
      var lastRect = last.getBoundingClientRect();
      var containerRect = dotsContainer.getBoundingClientRect();

      var firstCenter = firstRect.left + firstRect.width / 2;
      var lastCenter = lastRect.left + lastRect.width / 2;

      // Handle both LTR and RTL: take visual left/right, not DOM order
      var leftCenter = Math.min(firstCenter, lastCenter);
      var rightCenter = Math.max(firstCenter, lastCenter);

      var width = Math.max(0, rightCenter - leftCenter);
      var left = leftCenter - containerRect.left;

      dotsContainer.style.setProperty('--dots-line-width', width + 'px');
      dotsContainer.style.setProperty('--dots-line-left', left + 'px');
    }

    /* ---------- Dots: build + click scroll ---------- */
    function scrollToCard(card) {
      var trackRect = track.getBoundingClientRect();
      var cardRect = card.getBoundingClientRect();

      var trackCenter = trackRect.left + trackRect.width / 2;
      var cardCenter = cardRect.left + cardRect.width / 2;

      var delta = cardCenter - trackCenter;

      track.scrollBy({
        left: delta, // no RTL/LTR flip
        behavior: 'smooth',
      });
    }

    cards.forEach(function (card, index) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.setAttribute('aria-label', 'עבור להמלצה ' + (index + 1));
      dot.addEventListener('click', function () {
        scrollToCard(card);
      });
      dotsContainer.appendChild(dot);
      dots.push(dot);
    });

    // Compute side padding once we know layout
    updateTrackSidePadding();

    // Once dots are in the DOM, position the string correctly
    updateDotsLine();

    /* ---------- Slider state: active index + deck classes ---------- */
    function updateSliderState() {
      var trackRect = track.getBoundingClientRect();
      var trackCenter = trackRect.left + trackRect.width / 2;

      var newActiveIndex = 0;
      var minCenterDelta = Infinity;

      // How "wide" the deck effect feels
      var influenceRadius = trackRect.width * 0.7;

      // Separate tilt for left and right sides
      var maxTiltLeft = -10; // degrees
      var maxTiltRight = -8; // degrees

      var maxDrop = 22; // px down at the edges
      var maxScaleDrop = 0.16; // 1 - 0.16 = 0.84 at edges
      var maxFade = 0.2; // 1 -> 0.2 opacity at edges

      cards.forEach(function (card, index) {
        var rect = card.getBoundingClientRect();
        var cardCenter = rect.left + rect.width / 2;
        var delta = cardCenter - trackCenter; // >0 = to the right
        var absDelta = Math.abs(delta);

        if (absDelta < minCenterDelta) {
          minCenterDelta = absDelta;
          newActiveIndex = index;
        }

        // Normalised position: -1 (far left) .. 0 (center) .. +1 (far right)
        var t = delta / influenceRadius;
        if (t < -1) t = -1;
        if (t > 1) t = 1;
        var a = Math.abs(t); // 0 at center, 1 at edge

        // Side-specific base tilt
        var sideBaseTilt = t < 0 ? maxTiltLeft : maxTiltRight; // magnitude
        var rotate = sideBaseTilt * a * (t < 0 ? 1 : -1); // sign by side

        // Optional: add tiny stable jitter away from center
        var jitter = parseFloat(card.dataset.deckTiltJitter || '0');
        if (a > 0.2) {
          rotate += jitter;
        }

        var translateY = maxDrop * a;
        var scale = 1 - maxScaleDrop * a;
        var opacity = 1 - maxFade * a;

        card.style.transform =
          'translateY(' + translateY + 'px) rotate(' + rotate + 'deg) scale(' + scale + ')';
        card.style.opacity = opacity.toFixed(3);
      });

      activeIndex = newActiveIndex;

      // is-center just for things like shadow, not transform
      cards.forEach(function (card, index) {
        card.classList.toggle('is-center', index === activeIndex);
      });

      // Dots
      dots.forEach(function (dot, index) {
        dot.classList.toggle('is-active', index === activeIndex);
      });

      updateDotsLine();
    }

    // Initial state
    updateSliderState();

    // Update on scroll + resize
    track.addEventListener('scroll', function () {
      window.requestAnimationFrame(updateSliderState);
    });

    window.addEventListener('resize', function () {
      window.requestAnimationFrame(function () {
        updateTrackSidePadding();
        updateSliderState();
        updateDotsLine();
      });
    });

    /* ---------- Helpers for video state + progress loop ---------- */

    function triggerBounce(card) {
      var icon = card.querySelector('.dr-video-testimonials__play-icon');
      if (!icon) return;
      icon.classList.remove('is-bouncing');
      // Force reflow to restart animation
      void icon.offsetWidth;
      icon.classList.add('is-bouncing');
    }

    function stopProgressLoop(video) {
      if (video && video._progressRafId) {
        cancelAnimationFrame(video._progressRafId);
        video._progressRafId = null;
      }
    }

    function startProgressLoop(video, progress) {
      if (!video || !progress) return;

      stopProgressLoop(video);

      var step = function () {
        var dur = video.duration;
        if (dur && isFinite(dur) && dur > 0) {
          var pct = Math.max(0, Math.min(100, (video.currentTime / dur) * 100));
          progress.style.width = pct + '%';
        }
        if (!video.paused && !video.ended) {
          video._progressRafId = requestAnimationFrame(step);
        }
      };

      video._progressRafId = requestAnimationFrame(step);
    }

    function resetCard(card) {
      var video = card.querySelector('video[data-video]');
      var progress = card.querySelector('[data-video-progress]');
      if (video) {
        stopProgressLoop(video);
        video.pause();
        video.hidden = true;
      }
      if (progress) progress.style.width = '0%';
      card.classList.remove('is-playing', 'is-paused');
    }

    function pauseOtherVideos(exceptVideo) {
      cards.forEach(function (c) {
        var v = c.querySelector('video[data-video]');
        if (!v || v === exceptVideo) return;

        // Only reset videos that are currently playing
        if (!v.paused && !v.ended) {
          resetCard(c);
        }
      });
    }

    function togglePlayPause(card, video, progress) {
      if (video.paused) {
        // Resuming - delay hiding button until bounce animation finishes
        ensureVideoSrc(video);
        pauseOtherVideos(video);
        video.hidden = false;
        video.play();
        card.classList.remove('is-paused');
        startProgressLoop(video, progress);
        // Delay adding is-playing so bounce animation completes before button fades
        setTimeout(function () {
          card.classList.add('is-playing');
        }, 320);
      } else {
        video.pause();
        video.hidden = false; // keep current frame visible
        card.classList.remove('is-playing');
        card.classList.add('is-paused');
        stopProgressLoop(video);
      }
    }

    /* ---------- Lazy source loader ---------- */
    function ensureVideoSrc(video) {
      if (!video) return;
      // Look for a <source> that still has data-src
      var source = video.querySelector('source[data-src]');
      if (!source) return;

      var url = source.getAttribute('data-src');
      if (!url) return;

      // Swap data-src -> src and trigger load once
      source.removeAttribute('data-src');
      source.setAttribute('src', url);
      video.load();
    }

    /* ---------- Overlay click: start / resume ---------- */
    track.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-video-trigger]');
      if (!trigger) return;

      var card = trigger.closest('.dr-video-testimonials__card');
      var video = card && card.querySelector('video[data-video]');
      var progress = card && card.querySelector('[data-video-progress]');
      if (!video) return;

      triggerBounce(card);
      togglePlayPause(card, video, progress);
    });

    /* ---------- Per-card setup: tap layer + ended reset ---------- */
    cards.forEach(function (card) {
      var video = card.querySelector('video[data-video]');
      var tapLayer = card.querySelector('[data-video-tap]');
      var progress = card.querySelector('[data-video-progress]');
      if (!video || !tapLayer || !progress) return;

      // Reset on end
      video.addEventListener('ended', function () {
        resetCard(card);
      });

      // Tap layer: left/right seek, middle toggle
      tapLayer.addEventListener('click', function (event) {
        var rect = tapLayer.getBoundingClientRect();
        var x = event.clientX - rect.left;
        var quarter = rect.width / 4;

        // Left quarter: -5s
        if (x < quarter) {
          // Make sure the video has a real src before seeking (first interaction could be here)
          ensureVideoSrc(video);
          if (!isNaN(video.currentTime)) {
            video.currentTime = Math.max(0, video.currentTime - 5);
          }
          return;
        }

        // Right quarter: +5s
        if (x > 3 * quarter) {
          ensureVideoSrc(video);
          if (!isNaN(video.currentTime)) {
            var dur = video.duration || video.currentTime + 5;
            video.currentTime = Math.min(dur, video.currentTime + 5);
          }
          return;
        }

        // Middle half: toggle play/pause
        triggerBounce(card);
        togglePlayPause(card, video, progress);
      });
    });

    /* ---------- Auto-pause when card scrolls out of view ---------- */
    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var card = entry.target;
            var video = card.querySelector('video[data-video]');
            if (!video) return;

            // If card is mostly out of view and video is playing, pause it
            if (entry.intersectionRatio < 0.4 && !video.paused && !video.ended) {
              video.pause();
              video.hidden = false; // keep current frame on screen
              card.classList.remove('is-playing');
              card.classList.add('is-paused');
              stopProgressLoop(video);
            }
          });
        },
        {
          root: null, // viewport
          threshold: [0, 0.4],
        }
      );

      cards.forEach(function (card) {
        observer.observe(card);
      });
    }
  }

  /**
   * Initialize all testimonials within a context
   * @param {HTMLElement|Document} context - The context to search within
   */
  function initAllTestimonials(context) {
    var ctx = context || document;
    var roots = ctx.querySelectorAll('[data-dr-testimonials-root]');
    roots.forEach(initTestimonials);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllTestimonials(document);
    });
  } else {
    initAllTestimonials(document);
  }

  // Re-initialize on Shopify theme editor section reload
  document.addEventListener('shopify:section:load', function (event) {
    // Only init testimonials within the reloaded section
    if (event.target) {
      initAllTestimonials(event.target);
    }
  });
})();
